import React, { useState, useMemo } from 'react';
import { X as XIcon, Search, Sparkles, Check } from 'lucide-react';
import { SNAGCART_TEMPLATES, TEMPLATE_CATEGORIES } from '../../data/snagcartTemplates';

/**
 * TemplatesModal — full-screen modal that lets the user pick a professionally
 * designed Snagcart starter template. On select, it pre-fills the project
 * name + description and closes.
 */
export default function TemplatesModal({ open, onClose, onPick }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [hoveredId, setHoveredId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SNAGCART_TEMPLATES.filter((t) => {
      if (category !== 'all' && t.category !== category) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        (t.project_name || '').toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="templates-modal-overlay"
    >
      <div
        className="w-full max-w-6xl bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="templates-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/[0.06] px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold">Start from a template</h2>
              <p className="text-xs text-zinc-500">
                Professionally designed Snagcart starters. Pick one and we'll pre-fill the prompt.
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="templates-modal-close"
              className="p-2 rounded-lg hover:bg-white/[0.06] transition-colors"
              aria-label="Close templates"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Search + categories */}
          <div className="mt-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates…"
                data-testid="templates-search-input"
                className="w-full bg-[#0d0d0d] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {TEMPLATE_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`templates-cat-${c.id}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    category === c.id
                      ? 'border-violet-500/50 bg-violet-500/15 text-violet-200'
                      : 'border-white/[0.08] text-zinc-400 hover:text-zinc-200 hover:border-white/[0.16]'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="p-6">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500" data-testid="templates-empty">
              No templates match "{query}". Try a different search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((t) => {
                const hovered = hoveredId === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => onPick(t)}
                    onMouseEnter={() => setHoveredId(t.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    data-testid={`template-card-${t.id}`}
                    className="group relative text-left bg-[#0d0d0d] border border-white/[0.08] rounded-xl p-5 hover:border-violet-500/40 transition-all overflow-hidden"
                  >
                    {/* Accent gradient ribbon */}
                    <div
                      className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${t.accent} opacity-20 group-hover:opacity-30 blur-2xl transition-opacity`}
                      aria-hidden="true"
                    />
                    <div className="relative">
                      <div className="flex items-start justify-between gap-2">
                        <div
                          className={`w-10 h-10 rounded-lg bg-gradient-to-br ${t.accent} flex items-center justify-center text-lg shadow-md`}
                          aria-hidden="true"
                        >
                          <span>{t.emoji}</span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium px-2 py-1 rounded bg-white/[0.04] border border-white/[0.06]">
                          {t.category}
                        </span>
                      </div>
                      <h3 className="mt-3 text-sm font-semibold text-white">{t.name}</h3>
                      <p className="mt-1 text-xs text-zinc-400 line-clamp-2">{t.tagline}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-[11px] text-zinc-500 font-mono">{t.project_name}</span>
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-medium transition-opacity ${
                            hovered ? 'opacity-100 text-violet-300' : 'opacity-0'
                          }`}
                        >
                          <Check className="w-3 h-3" /> Use this
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 pb-5 text-[11px] text-zinc-600 border-t border-white/[0.04] pt-4">
          Tip: After picking a template you can still edit the prompt before hitting Start Build.
        </div>
      </div>
    </div>
  );
}
